import { useLingui } from '@lingui/react/macro';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import PauseIcon from '@mui/icons-material/Pause';
import PlayIcon from '@mui/icons-material/PlayArrow';
import { AppBar, Button, IconButton, Stack, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useLocation } from '@tanstack/react-router';
import React from 'react';
import { useDialogStore } from '@/components/dialogs';
import { KeyboardShortcutsDialog } from '../../features/shortcuts';
import { FeedbackButton } from '../../providers/usersnap/FeedbackButton.tsx';
import useAppStore from '../../store/rootStore.ts';
import { ConnectionStatus } from './ConnectionStatus';
import { SocialMediaLinks } from './social_links';
import { FooterTicker } from './ticker.tsx';

const FooterAppBar = styled(AppBar)<{ component?: React.ElementType }>(({ theme }) => ({
  top: 'auto',
  bottom: 0,
  height: theme.spacing(5),
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  gap: theme.spacing(2),
}));

export default function DexFooter() {
  const { t } = useLingui();
  const theme = useTheme();
  const features = useAppStore((state) => state.config?.features);
  // Below md the footer leaves the ticker ~90px, too little to read.
  const showTicker = useMediaQuery(theme.breakpoints.up('md'));
  const isTradePage = useLocation({ select: (location) => location.pathname.startsWith('/trade') });
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Pre-bootstrap config is undefined; `!== false` keeps that on the flag's default.
  const scrollingTicker = features?.animatedFooterTicker !== false && !reducedMotion;
  // The ticker only moves when the list is wider than its slot; nothing to pause otherwise.
  const [tickerOverflows, setTickerOverflows] = React.useState(false);
  const showTickerToggle = showTicker && scrollingTicker && tickerOverflows;
  const [isTickerPaused, setIsTickerPaused] = React.useState(false);

  const handleTickerToggle = React.useCallback(() => {
    setIsTickerPaused((paused) => !paused);
  }, []);

  const openDialog = useDialogStore((state) => state.openDialog);
  const handleShortcutsOpen = React.useCallback(() => openDialog('keyboardShortcutsDialog'), [openDialog]);

  // The mobile trade page keeps the bottom edge for its Buy/Sell bar.
  if (!showTicker && isTradePage) {
    return null;
  }

  return (
    <FooterAppBar component="footer">
      <Stack direction="row" alignItems="center" alignSelf="stretch" gap={1} flexShrink={0}>
        <ConnectionStatus />
        <FeedbackButton />
      </Stack>

      {/* Top Gainers Dropdown */}
      {/*<Select*/}
      {/*  value={topGainers}*/}
      {/*  onChange={handleTopGainersChange}*/}
      {/*  displayEmpty*/}
      {/*  variant="standard"*/}
      {/*  sx={{*/}
      {/*    '&:before, &:after': { border: 'none' },*/}
      {/*    '& .MuiSelect-select': {*/}
      {/*      py: 0,*/}
      {/*      pr: 0.5,*/}
      {/*      fontSize: '0.75rem',*/}
      {/*    },*/}
      {/*  }}*/}
      {/*  IconComponent={KeyboardArrowDownIcon}*/}
      {/*  inputProps={{ 'aria-label': 'Top Gainers' }}*/}
      {/*>*/}
      {/*  <MenuItem value="">Top Gainers</MenuItem>*/}
      {/*  <MenuItem value="daily">Daily Gainers</MenuItem>*/}
      {/*  <MenuItem value="weekly">Weekly Gainers</MenuItem>*/}
      {/*</Select>*/}

      {showTicker && (
        <FooterTicker scrolling={scrollingTicker} isPaused={isTickerPaused} onOverflowChange={setTickerOverflows} />
      )}

      <Stack direction="row" alignItems="center" gap={1}>
        {showTickerToggle ? (
          <IconButton
            size="small"
            onClick={handleTickerToggle}
            aria-label={
              isTickerPaused
                ? t({ comment: 'Footer ticker control', message: 'Resume ticker' })
                : t({ comment: 'Footer ticker control', message: 'Pause ticker' })
            }
          >
            {isTickerPaused ? <PlayIcon fontSize="inherit" /> : <PauseIcon fontSize="inherit" />}
          </IconButton>
        ) : null}
        <Button
          size="small"
          color="inherit"
          startIcon={<KeyboardIcon fontSize="small" />}
          onClick={handleShortcutsOpen}
          sx={{ typography: 'body2', minWidth: 0, display: { xs: 'none', md: 'inline-flex' } }}
        >
          {t({ comment: 'Footer button that opens the keyboard shortcuts guide', message: 'Shortcuts' })}
        </Button>
        <SocialMediaLinks docsUrl={features?.docsUrl} twitterUrl={features?.twitterUrl} discordUrl={features?.discordUrl} />
      </Stack>
      <KeyboardShortcutsDialog />
    </FooterAppBar>
  );
}
